// src/utils/apiUtils.js
// src/utils/apiUtils.js
import axios8001 from "../services/axios8001"; // For baseURL http://localhost:8001
import axios8200 from "../services/axios8200"; // For baseURL http://localhost:8200"

// Functions using axios8001 (http://localhost:8001)

// Fetch customer_id by customerName
export const fetchCustomerId = async (customerName) => {
  try {
    if (!customerName) {
      throw new Error("customerName is required to fetch customer_id");
    }
    const response = await axios8001.get(`/get_customer_id`, { params: { customer_name: customerName } });
    return response.data.customer_id;
  } catch (error) {
    console.error("Error fetching customer ID:", error);
    throw error;
  }
};

// Fetch opportunity_id by customerId, opportunityName, userId, and apiKey
export const fetchOpportunityId = async (customerId, opportunityName, userId, apiKey) => {
  try {
    if (!customerId || !opportunityName) {
      throw new Error("customerId and opportunityName are required to fetch opportunity_id");
    }
    const response = await axios8001.get(`/get_opportunity_id`, {
      params: { customer_id: customerId, opportunity_name: opportunityName, user_id: userId, api_key: apiKey },
    });
    return response.data.opportunity_id;
  } catch (error) {
    console.error("Error fetching opportunity ID:", error);
    throw error;
  }
};

// Fetch customer details by customerName
export const fetchCustomerDetails = async (customerName) => {
  try {
    if (!customerName) {
      throw new Error("customerName is required to fetch customer details");
    }
    const response = await axios8001.get("/get_customer_details", {
      params: { customer_name: customerName },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching customer details:", error);
    throw error;
  }
};

// Fetch account_id by customerId and customerName
export const fetchAccountId = async (customerId, customerName) => {
  try {
    if (!customerId || !customerName) {
      throw new Error("Both customerId and customerName are required to fetch account_id");
    }
    const response = await axios8001.get("/get_account_id", {
      params: { customer_id: customerId, account_name: customerName },
    });
    return response.data.account_id;
  } catch (error) {
    console.error("Error fetching account ID:", error);
    throw error;
  }
};

// Functions using axios8200 (http://localhost:8200)

// Fetch account learnings by accountId
export const fetchAccountLearnings = async (accountId) => {
  try {
    if (!accountId) {
      throw new Error("accountId is required to fetch account learnings");
    }
    const response = await axios8200.get(`/get_account_learnings`, {
      params: { account_id: accountId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching account learnings:", error);
    throw error;
  }
};

// Fetch delayed tasks by role and accountId
export const fetchDelayedTasksByRole = async (accountId) => {
  try {
    if (!accountId) {
      throw new Error("accountId is required to fetch delayed tasks");
    }
    const response = await axios8200.get(`/get_delayed_tasks_by_role`, {
      params: { account_id: accountId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching delayed tasks by role:", error);
    throw error;
  }
};

// Fetch opportunity metrics by opportunityId
// Fetch opportunity metrics
export const fetchOpportunityMetrics = async (opportunityId) => {
  if (!opportunityId) {
    console.error("Opportunity ID is required for fetchOpportunityMetrics");
    return { total_tasks: 0, completed_tasks: 0 };
  }
  try {
    const response = await axios8200.get(`/task_metrics/${opportunityId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching opportunity metrics:", error);
    return { total_tasks: 0, completed_tasks: 0 };
  }
};

// Fetch closing probability
export const fetchClosingProbability = async (opportunityId) => {
  if (!opportunityId) {
    console.error("Opportunity ID is required for fetchClosingProbability");
    return { closing_probability: 0 };
  }
  try {
    const response = await axios8200.get(`/closing_probability/${opportunityId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching closing probability:", error);
    return { closing_probability: 0 };
  }
};

// Fetch opportunity learnings
export const fetchOpportunityLearnings = async (opportunityId, customerId, opportunityName) => {
  try {
    // Fetch `opportunityId` if not provided but `customerId` and `opportunityName` are available
    if (!opportunityId && customerId && opportunityName) {
      opportunityId = await fetchOpportunityId(customerId, opportunityName);
    }

    if (!opportunityId) {
      console.error("Opportunity ID is required for fetchOpportunityLearnings");
      return { learnings: [] }; // Default to empty learnings
    }

    const response = await axios8200.get(`/get_opportunity_learnings`, {
      params: { opportunity_id: opportunityId },
    });

    return response.data || { learnings: [] }; // Ensure a fallback structure
  } catch (error) {
    console.error(`Error fetching learnings for Opportunity ID: ${opportunityId}`, error);
    return { learnings: [] }; // Default to avoid breaking logic
  }
};

// Unified fetch function for closing probabilities of all opportunities
export const fetchAllClosingProbabilities = async (customerOpportunities) => {
  if (!customerOpportunities || Object.keys(customerOpportunities).length === 0) {
    console.warn("No customer opportunities provided for fetchAllClosingProbabilities");
    return {}; // Return an empty object if no opportunities are available
  }

  try {
    const probabilities = {};

    // Iterate over all customer opportunities
    const promises = Object.entries(customerOpportunities).flatMap(([customerName, opportunities]) =>
      opportunities.map(async (opportunity) => {
        const { opportunity_id } = opportunity;

        if (!opportunity_id) {
          console.warn(`Skipping opportunity for ${customerName}: Missing opportunity_id`);
          return;
        }

        try {
          const data = await fetchClosingProbability(opportunity_id);
          probabilities[opportunity_id] = data.closing_probability || 0; // Store fetched probability
        } catch (error) {
          console.error(`Failed to fetch closing probability for Opportunity ID: ${opportunity_id}`, error);
          probabilities[opportunity_id] = 0; // Default to 0 on failure
        }
      })
    );

    await Promise.all(promises); // Wait for all promises to resolve
    return probabilities; // Return the accumulated probabilities
  } catch (error) {
    console.error("Error fetching all closing probabilities:", error);
    return {}; // Return an empty object on error
  }
};
